import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {PageTitleService} from "../../shared/services/page-title.service";
import {UnitCallEventsFilters} from "../../units/models/unit-call-events-filters";
import {CallEvent} from "../../units/models/call-event";
import {Observable, Subject, takeUntil} from 'rxjs';
import {FarfisaDevice} from "../../shared/models/devices/farfisa_device";
import {IotService} from "../../shared/services/iot.service";
import {BaseDashboardComponent} from "../../shared/components/base-dashboard.component";
import {LocalizerService, ScreenService} from "../../shared";
import {Pbx} from "../models/pbx";
import {Session} from "../../shared/models/webrtc/call/call";
import {PbxManager} from "../services/pbx-manager";
import {UnitDashboard} from "../../units/models/unit-dashboard";
import {CallSessionManager} from "../../shared/services/call-session-manager.service";
import {CallEntity, CallEntityKind, CallParticipant} from "../../shared/models/webrtc/call/v2/call-entity";
import { Store } from '@ngrx/store';
import { selectCurrentCall, selectCallForEntity } from '../../state/call/current-call.selector';
import { CallEntry, CallLeg } from '../../state/call/current-call-state';
import { CurrentCallActions } from '../../state/call/current-call.actions';
import { DeviceStatus } from '../../shared/models/shadow/device-shadow';

@Component({
    selector: 'app-pbx-dashboard',
    templateUrl: './pbx-dashboard.component.html',
    styleUrl: './pbx-dashboard.component.scss'
})
export class PbxDashboardComponent extends BaseDashboardComponent implements OnInit, OnChanges, AfterViewInit {

    @Input() pbxId: string | null = null;

    pbx: Pbx | null = null;

    // Debug: full call state
    debugCallState: CallEntry | null = null;
    private entitySub$ = new Subject<void>();
    private lastScrolledCalleeId: string | null = null;

    // Derived from Redux state
    // When on hold: mainLeg is undefined, pipLeg has status 'hold'
    get currentCallOnHold(): boolean {
        return this.debugCallState?.pipLeg?.status === 'hold' && !this.debugCallState?.mainLeg;
    }

    get entityOnHold(): CallEntity | null | undefined {
        return this.debugCallState?.pipLeg?.status === 'hold'
            ? this.debugCallState.pipLeg.entity
            : null;
    }

    /** The original callee of the incoming call (who the caller intended to reach) */
    get incomingCalleeId(): string | null {
        return this.debugCallState?.calleeId ?? null;
    }

    /** The active leg: mainLeg if exists (speaking/outgoing), otherwise pipLeg if not on hold */
    private get activeLeg() {
        const s = this.debugCallState;
        // mainLeg is the primary active leg (new call when forwarding, or normal call)
        if (s?.mainLeg) {
            return s.mainLeg;
        }
        // pipLeg only if it's not on hold (should not happen normally)
        if (s?.pipLeg && s.pipLeg.status !== 'hold') {
            return s.pipLeg;
        }
        return null;
    }

    constructor(screen: ScreenService, localizer: LocalizerService, private pbxManager: PbxManager,
        store: Store, callSessionManager: CallSessionManager, iot: IotService,
        private pageTitle: PageTitleService, private el: ElementRef) {
        super(screen, localizer, callSessionManager, iot, store);
    }

    ngOnInit(): void {
        if (!this.installationId || !this.pbxId) {
            return;
        }

        this.setupEntitySubscriptions();

        this.store.select(selectCurrentCall).pipe(
            takeUntil(this.onDestroy)
        ).subscribe(currentCallState => {
            this.debugCallState = currentCallState;

            if (!currentCallState) {
                this.currentCallingEntity = null;
                this.currentCallingEntityName = null;
                this.currentCallingEntityOutputs = undefined;
                this.currentCallingEntityThingName = null;
                return;
            }

            // Sync local variables with Redux state for template compatibility
            const leg = this.activeLeg;

            this.currentCallingEntity = leg?.entity ?? null;
            this.currentCallingEntityName = leg?.entityName || this.resolveCallerName(leg?.entity) || null;
            this.currentCallingEntityOutputs = leg?.entityOutputs;
            this.currentCallingEntityThingName = leg?.entityThingName ?? null;

            // Enrich Redux with local dashboard data if entity info is missing
            this.enrichLegEntityInfo(currentCallState.callId!, leg);

            // Scroll to the callee unit in the contacts list
            if (currentCallState.calleeId && currentCallState.calleeId !== this.lastScrolledCalleeId) {
                this.lastScrolledCalleeId = currentCallState.calleeId;
                this.scrollToCallee(currentCallState.calleeId);
            }
        });

        this.callSessionManager.onCallEnd().pipe(takeUntil(this.onDestroy)).subscribe(callSession => {
            if (callSession.callId == this.incomingCall?.id) {
                this.incomingCallCardVisible = false;
                this.incomingCall = null;
            }
        });

        this.loadDashboard();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['pbxId'] && !changes['pbxId'].firstChange) {
            this.setupEntitySubscriptions();
            this.loadDashboard();
        }
    }

    ngAfterViewInit() {
        this.checkForAnsweringCallSession(this.pbxId!);
    }

    private setupEntitySubscriptions(): void {
        // Unsubscribe from previous entity-specific subscriptions
        this.entitySub$.next();

        if (!this.pbxId) return;

        // Set the call for this entity as active (so device-live-view picks it up)
        this.store.select(selectCallForEntity(this.pbxId)).pipe(
            takeUntil(this.entitySub$),
            takeUntil(this.onDestroy)
        ).subscribe(call => {
            if (call?.callId) {
                this.store.dispatch(CurrentCallActions.setActiveCall({ callId: call.callId }));
            }
        });

        // Re-check for answering call session
        this.checkForAnsweringCallSession(this.pbxId);
    }

    protected buildCaller(): CallParticipant {
        return {
            id: this.pbxId!,
            kind: CallEntityKind.PBX
        };
    }

    viewIncomingCall(session: Session, answer: boolean) {
        this.incomingCallCardVisible = false;
        this.incomingCall = null;

        if (answer) {
            this.callSessionManager.startDeviceTalkSession(session.callId, session.sessionId);
        }
    }

    pbxEventsProvider = (args: {
        installationId: string,
        entityId: string,
        pagination?: any,
        filters?: UnitCallEventsFilters
    }): Observable<CallEvent[]> => {
        return this.pbxManager.getPbxCallEvents(args.installationId, args.entityId, args.pagination, args.filters);
    };

    //#region Unit items methods

    unitCallButtonClick(unit: UnitDashboard) {
        const callee: CallParticipant = {
            id: unit.id,
            kind: CallEntityKind.Unit,
            name: unit.name
        };
        this.startOrForwardCall(callee);
    }

    isEntityOnHold(entityId: string, entityKind: CallEntityKind): boolean {
        return this.entityOnHold?.id === entityId && this.entityOnHold?.kind === entityKind;
    }

    //#endregion Unit items methods

    //#region Device card methods

    deviceLiveViewButtonClick(device: FarfisaDevice) {
        const callee: CallParticipant = {
            id: device.id,
            kind: CallEntityKind.Doorstation,
            name: device.name,
            outputs: device.available_outputs,
            thingName: device.thing_name
        };
        this.startOrForwardCall(callee);
    }

    /**
     * If a call is on hold, forward to the callee.
     * Otherwise, start a new outgoing call.
     */
    private startOrForwardCall(callee: CallParticipant): void {
        if (this.currentCallOnHold && this.debugCallState?.callId) {
            // Forward the held call to the new callee
            this.callSessionManager.forwardCall(
                this.debugCallState.callId,
                this.pbxId!,
                CallEntityKind.PBX,
                callee.id,
                callee.kind,
                this.installationId!
            );
        } else {
            // Start a new outgoing call
            this.startOutgoingCall(callee);
        }
    }

    onDeviceRowPrepared(e: any) {
        if (e.rowType === 'data' && this.isEntityOnHold(e.data.id, CallEntityKind.Doorstation)) {
            e.rowElement.classList.add('device-row--disabled');
        }
    }

    //#endregion device card methods

    private loadDashboard(): void {
        if (!this.installationId || !this.pbxId) {
            return;
        }

        // Reset state for the new dashboard
        this.pbx = null;
        this.devices = [];
        this.shadowUpdates = [];
        this.pageTitle.setTitle(null);

        this.isBusy = true;
        this.pbxManager.getPbxDashboard(this.installationId, this.pbxId).subscribe({
            next: (dashboard) => {
                this.isBusy = false;
                this.pbxDashboardRetrieved(dashboard);
            },
            error: () => this.isBusy = false
        });
    }

    private pbxDashboardRetrieved(dashboard: Pbx) {
        if (!dashboard) {
            return;
        }

        this.pbx = dashboard;

        if (this.pbx.devices) {
            this.devices = this.pbx.devices;
            this.subscribeToDeviceShadows();
        }

        this.pageTitle.setTitle(this.pbx.name ?? this.pbx.alias ?? null);
        this.enrichActiveCallFromDevices();
    }

    /**
     * Resolve caller name locally from dashboard data (devices/units).
     */
    private resolveCallerName(entity: { id: string; kind: CallEntityKind } | null | undefined): string | null {
        if (!entity || !this.pbx) return null;

        if (entity.kind === CallEntityKind.Doorstation) {
            const device = this.pbx.devices?.find(d => d.id === entity.id) as FarfisaDevice;
            if (device) {
                this.currentCallingEntityOutputs = device.available_outputs;
                this.currentCallingEntityThingName = device.thing_name;
                return device.name;
            }
        } else if (entity.kind === CallEntityKind.Unit) {
            const unit = this.pbx.units?.find(u => u.id === entity.id);
            if (unit) {
                return unit.name ?? null;
            }
        }
        return null;
    }

    /** Override to also handle Unit entities from PBX managed_units */
    protected override enrichLegEntityInfo(callId: string, leg: CallLeg | undefined | null): void {
        super.enrichLegEntityInfo(callId, leg);

        if (!callId || !leg || leg.entityName || !this.pbx) return;

        const sessionId = leg.sessions[0]?.sessionId;
        if (!sessionId) return;

        const entity = leg.entity;
        if (entity?.kind === CallEntityKind.Unit) {
            const unit = this.pbx.managed_units?.find(u => u.id === entity.id);
            if (unit?.name) {
                this.store.dispatch(CurrentCallActions.updateSessionEntityInfo({
                    callId,
                    sessionId,
                    entityName: unit.name,
                }));
            }
        }
    }

    private scrollToCallee(calleeId: string): void {
        setTimeout(() => {
            const element = this.el.nativeElement.querySelector(`[data-unit-id="${calleeId}"]`);
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }

    // Make enum accessible in template
    protected readonly CallEntityKind = CallEntityKind;
    protected readonly DeviceStatus = DeviceStatus;
}
